/**
 * Remote Storage Backend
 * Ported from: /Users/victorvins/Projects/application/app/scripts/storage/RemoteStorage.js
 * Also references: innovation-claude/packages/storage/src/backends/remote.rs
 *
 * Uses Tradovate storage HTTP API via fetch
 */

import { StorageError } from '../StorageError'

/**
 * RemoteBackend - Uses Tradovate storage HTTP API
 *
 * URL Pattern:
 * - GET: {baseUrl}user/{userId}/items/{key}
 * - POST: {baseUrl}user/{userId}/items (with {"items": {key: data}} body)
 * - DELETE: {baseUrl}user/{userId}/items (with {"items": {key: null}} body)
 *
 * Example: https://config-d.tradovateapi.com/user/58/items/workspaces
 *
 * Authorization header format: Bearer {username} {token}
 * Example: Bearer gvic eyJraWQiOiIxMCIsImFsZyI6IkVkRFNBIn0...
 *
 * NOTE: Uses functions for auth (not static values) to support token renewal
 */
export class RemoteBackend {
    constructor(
        private baseUrl: string,
        private getAuthToken: () => string,
        private getUserId: () => string,
        private getUsername: () => string
    ) {
        // Normalize base URL to ensure trailing slash
        if (this.baseUrl && !this.baseUrl.endsWith('/')) this.baseUrl = this.baseUrl + '/'
    }

    /**
     * Build full URL for GET/DELETE operations
     * Pattern: {baseUrl}user/{userId}/items/{key}
     * From RemoteStorage.js:48
     */
    private buildItemUrl(key: string): string {
        const userId = this.getUserId()
        return `${this.baseUrl}user/${userId}/items/${key}`
    }

    /**
     * Build URL for POST/DELETE operations
     * Pattern: {baseUrl}user/{userId}/items
     */
    private buildItemsUrl(): string {
        const userId = this.getUserId()
        return `${this.baseUrl}user/${userId}/items`
    }

    /**
     * Save JSON data to remote storage
     * Uses POST to {baseUrl}user/{userId}/items
     */
    async save(key: string, json: string): Promise<void> {
        const url = this.buildItemsUrl()
        const token = this.getAuthToken()
        const username = this.getUsername()

        // Parse JSON to get the data object
        const data = JSON.parse(json)

        // Build payload: { items: { [key]: data } }
        const payload = {
            items: {
                [key]: data
            }
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${username} ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        })

        if (!response.ok) {
            throw StorageError.network(
                `HTTP ${response.status}: ${response.statusText}`,
                response.status
            )
        }
    }

    /**
     * Load JSON data from remote storage
     * Uses GET from {baseUrl}user/{userId}/items/{key}
     * Returns null if not found (404)
     * From RemoteStorage.js:43-52,70-93
     */
    async load(key: string): Promise<string | null> {
        const url = this.buildItemUrl(key)
        const token = this.getAuthToken()
        const username = this.getUsername()

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${username} ${token}`
            }
        })

        if (response.status === 404 || response.status === 204) {
            return null  // Not found or no content
        }

        if (!response.ok) {
            throw StorageError.network(
                `HTTP ${response.status}: ${response.statusText}`,
                response.status
            )
        }

        const text = await response.text()
        return text || null
    }

    /**
     * Delete data from remote storage
     * Sends null value to items endpoint
     */
    async delete(key: string): Promise<void> {
        const url = this.buildItemsUrl()
        const token = this.getAuthToken()
        const username = this.getUsername()

        // Delete by sending null value
        const payload = {
            items: {
                [key]: null
            }
        }

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${username} ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        })

        // Success or already deleted
        if (response.ok || response.status === 404) {
            return
        }

        throw StorageError.network(
            `HTTP ${response.status}: ${response.statusText}`,
            response.status
        )
    }
}
